import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Mail, Phone } from 'lucide-react';

const Header = ({ t, language, setLanguage, scrollToSection }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navDropdownOpen, setNavDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = useCallback((sectionId) => {
    scrollToSection(sectionId);
    setNavDropdownOpen(false);
    setMenuOpen(false);
  }, [scrollToSection]);

  const handleLogoClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleLogoKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleLanguageChange = useCallback((lang) => {
    setLanguage(lang);
    setMenuOpen(false);
  }, [setLanguage]);

  const languages = ['bg', 'en', 'fr', 'de'];

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''}`} role="banner">
      <div className="header-content">
        <div className="header-left">
          <img 
            src="https://customer-assets.emergentagent.com/job_cage-landing/artifacts/fjfp113m_Logo.svg" 
            alt="UrbanCage Logo" 
            className="logo"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={handleLogoKeyDown}
            width="140"
            height="40"
            data-testid="header-logo"
          />
          <nav className="nav-dropdown-container" aria-label={language === 'bg' ? 'Главна навигация' : 'Main navigation'}>
            <button 
              className="nav-dropdown-trigger"
              onClick={() => setNavDropdownOpen(!navDropdownOpen)}
              aria-expanded={navDropdownOpen}
              aria-label={language === 'bg' ? 'Отвори менюто' : 'Open menu'}
              data-testid="nav-dropdown-trigger"
            >
              <Menu size={22} />
            </button>
            
            {navDropdownOpen && (
              <div className="nav-dropdown" role="menu">
                <button role="menuitem" onClick={() => handleNavClick('intro')}>{t.whatIsUrbanCage}</button>
                <button role="menuitem" onClick={() => handleNavClick('who-for')}>{t.whoForTitle}</button>
                <button role="menuitem" onClick={() => handleNavClick('patented')}>{t.patentedTitle}</button>
                <button role="menuitem" onClick={() => handleNavClick('models')}>{t.modelSizeTitle}</button>
                <button role="menuitem" onClick={() => handleNavClick('gallery')}>{t.galleryTitle}</button>
                <button role="menuitem" onClick={() => handleNavClick('video')}>{t.videoTitle}</button>
                <button role="menuitem" onClick={() => handleNavClick('contact')}>{t.contactTitle}</button>
              </div>
            )}
          </nav>
        </div>
        
        <div className="header-right">
          <div className="contact-info-inline">
            <a href={`mailto:${t.email}`} className="contact-link" data-testid="header-email-link">
              <Mail size={16} />
              <span>{t.email}</span>
            </a>
            <span className="contact-divider">|</span>
            <a href={`tel:${t.phone}`} className="contact-link" data-testid="header-phone-link">
              <Phone size={16} />
              <span>{t.phone}</span>
            </a>
          </div>

          <div className="language-selector desktop-only" role="group" aria-label="Language selector">
            {languages.map((lang) => (
              <button
                key={`lang-desktop-${lang}`}
                className={`lang-btn ${language === lang ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang)}
                aria-pressed={language === lang}
                data-testid={`lang-btn-${lang}`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          <button 
            className="menu-btn mobile-only" 
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <nav className="mobile-menu" role="navigation" aria-label="Mobile navigation">
          <button onClick={() => handleNavClick('intro')}>{t.whatIsUrbanCage}</button>
          <button onClick={() => handleNavClick('who-for')}>{t.whoForTitle}</button>
          <button onClick={() => handleNavClick('patented')}>{t.patentedTitle}</button>
          <button onClick={() => handleNavClick('models')}>{t.modelSizeTitle}</button>
          <button onClick={() => handleNavClick('gallery')}>{t.galleryTitle}</button>
          <button onClick={() => handleNavClick('video')}>{t.videoTitle}</button>
          <button onClick={() => handleNavClick('contact')}>{t.contactTitle}</button>
          
          {/* Language selector in mobile menu */}
          <div className="mobile-language-selector" role="group" aria-label="Language selector">
            {languages.map((lang) => (
              <button
                key={`lang-mobile-${lang}`}
                className={`mobile-lang-btn ${language === lang ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang)}
                aria-pressed={language === lang}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default React.memo(Header);
